import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

import Container from '../components/layout/Container';
import SectionTitle from '../components/ui/SectionTitle';
import Button from '../components/ui/Button';
import CTASection from '../components/ui/CTASection';

const PricingPage: React.FC = () => {
  const pricingTiers = [
    {
      name: 'Core',
      id: 'core',
      description: 'Essential tools to get started with IDX and build your online presence.',
      features: [
        'Standard IDX search integration',
        'Essential plug-and-play widgets',
        'Basic customizable map search',
        'My Listings Manager',
        'Limited lead capture tools',
        'Basic contact form',
        'Mobile-optimized design',
        'Light SEO tuning',
        'IDX subdomain',
        'Premium support',
      ],
    },
    {
      name: 'Engage',
      id: 'engage',
      description: 'Grow your audience and engagement with automation and branding tools.',
      popular: true,
      features: [
        'All Core features',
        'Market Reports',
        'Sold Data (MLS permitting)',
        'AI Smart Search',
        'AI Listing Descriptions',
        'Trigger-based lead forms',
        'CRM integration options',
        'Enhanced SEO',
        'Optional custom domain',
        'Embeddable IDX widgets',
      ],
    },
    {
      name: 'Elite',
      id: 'elite',
      description: 'A premium solution for top agents, teams, and luxury-focused brands.',
      features: [
        'All Engage features',
        'Full custom-branded website',
        'Custom homepage layout',
        'Full-screen listing showcase',
        'Video background support',
        'Advanced automation triggers',
        'Custom map search filters',
        'Direct social media sync',
        'Blog & content manager',
        'Integrated hosting + CDN',
      ],
    },
  ];

  const faqs = [
    {
      question: 'Do I need technical skills to set up my IDX website?',
      answer: 'Not at all! Our platform is built for non-technical users. We also offer guided onboarding.',
    },
    {
      question: 'Can I use my own domain name?',
      answer: 'Yes! Engage and Elite plans support custom domains. Core plans include a free subdomain.',
    },
    {
      question: 'Is there a setup fee?',
      answer: 'Every IDX site is quoted for the agent or team after a short call, and the quote includes onboarding and MLS configuration.',
    },
    {
      question: 'Can I change my plan later?',
      answer: 'Yes. You can upgrade anytime. Downgrades take effect at the end of your billing cycle.',
    },
    {
      question: 'How frequently is listing data updated?',
      answer: 'We sync multiple times daily via the FMLS Bridge API to keep data accurate and fresh.',
    },
    {
      question: 'How is pricing set?',
      answer: 'Each plan is quoted for the agent or team after a short call. Contact us to request a quote or a demo.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>IDX Pricing | rAIn Automation</title>
        <meta
          name="description"
          content="Flexible IDX plans for every real estate agent. Compare Core, Engage, and Elite tiers with features like AI tools, SEO, lead capture, and custom websites."
        />
        <link rel="canonical" href="https://idx.rainautomation.com/pricing" />
      </Helmet>

      <section className="pt-32 pb-16">
        <Container>
          <SectionTitle
            title="Plans Quoted for Your Business"
            subtitle="Compare the plans, then talk with us for a quote"
            centered
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={index}
                className={`bg-white rounded-lg overflow-hidden h-full flex flex-col border ${tier.popular ? 'border-2 border-primary-500 shadow-premium' : 'border-neutral-200 shadow-soft'}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                {tier.popular && (
                  <div className="bg-primary-500 text-white text-center py-1 text-sm font-medium">Most Popular</div>
                )}
                <div className="p-6 md:p-8 flex-grow">
                  <h2 className="text-2xl font-semibold mb-2">{tier.name}</h2>
                  <p className="text-neutral-600 mb-6">{tier.description}</p>
                  <p className="text-sm text-neutral-500 mb-6">Quoted per agent or team</p>
                  <div className="flex flex-col gap-3">
                    <Button variant="primary" fullWidth size="lg" to="/contact">
                      Contact for Pricing
                    </Button>
                    <Button variant="outline" fullWidth size="lg" to="/contact">
                      Request a Demo
                    </Button>
                  </div>
                  <hr className="my-6 border-neutral-200" />
                  <ul className="space-y-3">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-primary-500 flex-shrink-0 mr-2">
                          <Check size={18} />
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 bg-neutral-50">
        <Container>
          <h2 className="text-3xl font-bold font-serif mb-12 text-center">Frequently Asked Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                className="bg-white p-6 rounded-lg shadow-soft"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <h3 className="text-xl font-semibold mb-3">{faq.question}</h3>
                <p className="text-neutral-600">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 bg-white">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold font-serif mb-6">Need a Custom Solution?</h2>
            <p className="text-xl text-neutral-600 mb-8">
              We offer enterprise IDX solutions tailored to brokerages, franchises, and MLS organizations.
            </p>
            <Button variant="primary" size="lg" to="/contact">
              Contact Our Sales Team
            </Button>
          </div>
        </Container>
      </section>

      <CTASection
        title="Ready to Get Started?"
        subtitle="Tell us about your business and we will quote the plan that fits."
        primaryButtonText="Request a Demo"
        secondaryButtonText="Contact for Pricing"
        primaryButtonTo="/contact"
        secondaryButtonTo="/contact"
        background="gradient"
      />
    </>
  );
};

export default PricingPage;
